import * as pptxtojson from 'pptxtojson'
import { read, utils } from 'xlsx'
import mammoth from 'mammoth'
import {
  MAX_INLINE_TEXT_BYTES,
  MAX_INLINE_TEXT_LABEL,
  MAX_UPLOAD_FILE_BYTES,
  MAX_UPLOAD_FILE_LABEL,
} from '@highlight/common/product'

const extractTextFromPowerPoint = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer()
  const json = await pptxtojson.parse(arrayBuffer)

  const cleanText = (text: string): string => {
    return text
      .replace(/&nbsp;/g, ' ')
      .replace(/<\/?[^>]+(>|$)/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const slideText = json.slides
    .map((slide, index) => {
      const slideNumber = index + 1
      const slideContent = slide.elements
        .filter((e) => e.type === 'text')
        .map((e) => cleanText(e.content))
        .filter((text) => text.length > 0)
        .join('\n')

      return slideContent.length > 0 ? `[Slide ${slideNumber}]\n${slideContent}` : ''
    })
    .filter((text) => text.length > 0)
    .join('\n\n')

  return slideText
}

const extractTextFromExcel = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer()
  const workbook = read(arrayBuffer, { type: 'array', WTF: true })
  let text = ''

  workbook.SheetNames.forEach((sheetName, index) => {
    const worksheet = workbook.Sheets[sheetName]
    const sheetData = utils.sheet_to_csv(worksheet)

    // Add sheet name and separator
    text += `[Sheet ${index + 1}: ${sheetName}]\n`
    text += sheetData
    text += '\n\n' // Add extra newline for separation between sheets
  })

  return text.trim() // Remove trailing newlines
}

const readTextFile = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target?.result as string)
    reader.onerror = (e) => reject(e)
    reader.readAsText(file)
  })
}

const readFileAsDataURL = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target?.result as string)
    reader.onerror = (e) => reject(e)
    reader.readAsDataURL(file)
  })
}

export type ProcessedFile = {
  file: File
  value: string | ArrayBuffer
  /** User-facing reason the file can't be attached. Set instead of `value`. */
  error?: string
}

const isDataUrl = (value: string) => value.startsWith('data:')

/**
 * Why `file` can't be attached, or null when it can. Mirrors the gateway's
 * limits: uploaded bytes are capped per file, and text that rides inline with
 * the message is capped by what the chat backend can persist. Inline text is
 * measured as the JSON-encoded UTF-8 bytes the backend measures, not as JS
 * characters.
 */
export const attachmentSizeError = (file: File, value: string | ArrayBuffer | null): string | null => {
  // `>=` matches the gateway's `/files/prepare` check, so the notice's "under" is exact.
  if (file.size >= MAX_UPLOAD_FILE_BYTES) {
    return `“${file.name}” is too large. Attachments must be under ${MAX_UPLOAD_FILE_LABEL}.`
  }
  if (typeof value === 'string' && !isDataUrl(value)) {
    const inlineBytes = new TextEncoder().encode(JSON.stringify(value)).byteLength
    if (inlineBytes > MAX_INLINE_TEXT_BYTES) {
      return `“${file.name}” has too much text. Text attachments must be under ${MAX_INLINE_TEXT_LABEL}.`
    }
  }
  return null
}

export const processFile = async (file: File): Promise<ProcessedFile> => {
  const fileType = file.type
  console.log('Extracting text from:', file.name, fileType)

  const oversized = attachmentSizeError(file, null)
  if (oversized) {
    return { file, value: '', error: oversized }
  }

  try {
    let value: string | ArrayBuffer = ''

    if (fileType.includes('spreadsheetml') || fileType.includes('excel')) {
      value = await extractTextFromExcel(file)
    } else if (fileType === 'application/pdf') {
      value = await file.arrayBuffer() // PDF is converted to txt upon upload
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
      value = await extractTextFromPowerPoint(file)
    } else if (
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileType === 'application/msword'
    ) {
      const arrayBuffer = await file.arrayBuffer()
      const result = await mammoth.extractRawText({ arrayBuffer })
      value = result.value
    } else if (fileType.includes('application/') || fileType.includes('text/')) {
      value = await readTextFile(file)
    } else if (fileType.includes('image/')) {
      // Keep image handling as base64
      value = await readFileAsDataURL(file)
    } else {
      console.warn('Unexpected File Type. Attempting to parse as text:', fileType, file.name)
      value = await readTextFile(file)
      console.log('File value: ', value.slice(0, 100))
    }

    const tooMuchText = attachmentSizeError(file, value)
    if (tooMuchText) {
      return { file, value: '', error: tooMuchText }
    }
    if (typeof value === 'string' && !value.length) {
      return { file, value, error: `“${file.name}” is empty or couldn’t be read.` }
    }

    return {
      file,
      value,
    }
  } catch (error) {
    console.error('Error processing file:', error)
    return {
      file,
      value: '',
      error: `“${file.name}” couldn’t be read.`,
    }
  }
}

export const shouldPasteAsAttachment = (text: string) => {
  const newLines = text.split('\n').length
  if (newLines >= 20) {
    return true
  }
  return text.length > 1000
}
